import React from "react"
import { motion, Variants } from "framer-motion"
import TitleHeader from '../../atoms/TitleHeader/TitleHeader'
import Constants from '../../../app/utils/constants'
import colors from '../../theme/color'

type WorkProps = {
  showComposition?: boolean;
  showHowWeWork?: boolean;
};

const Work = ({ showComposition = true, showHowWeWork = true }: WorkProps) => {
  const { workFirstSectionCards, workSecondSectionCards } = Constants

  // 🔥 Animation Variants
const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
}

const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut", 
    },
  },
}

  return (
    <section className="w-full px-4 pb-14 sm:px-6 md:px-10 lg:px-16 lg:pb-20">

      {/* Marquee animation */}
      <style>
        {`
          @keyframes workMarquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333333%); }
          }
        `}
      </style>

      <div className="mx-auto w-full max-w-6xl space-y-16">

        {/* ================= FIRST SECTION ================= */}
        {showComposition && (
        <div>
          <h2 className="sm:w-full md:w-[70%] lg:w-[55%] text-center mx-auto font-rajdhani text-3xl font-semibold text-white sm:text-4xl md:text-4xl lg:text-5xl">
            Our team composition reflects our specialization:
          </h2>

          {/* Animated Grid */}
          <motion.div
            className="mt-10 grid grid-cols-2 justify-items-center gap-4 sm:gap-6 md:gap-8 lg:grid-cols-6 lg:gap-x-10 lg:gap-y-8 xl:gap-x-12 xl:gap-y-15 min-[2560px]:gap-x-16 min-[2560px]:gap-y-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
          >
            {workFirstSectionCards.map((card, index) => (
              
              <motion.div
                key={card.heading}
                variants={cardVariants}
                className={`rotating-border-gradient h-auto w-full max-w-[280px] rounded-[30px] p-px transition-transform duration-300 hover:scale-[1.04] sm:max-w-[300px] lg:col-span-2 lg:max-w-[320px] xl:max-w-[360px] ${
                  index === 3 ? 'lg:col-start-2' : ''
                } ${index === 4 ? 'lg:col-start-4' : ''}`}
              >
                <div
                  className="relative flex h-full w-full flex-col items-start gap-3 overflow-hidden rounded-[30px] p-4 text-left transition-all duration-300 sm:gap-4 sm:p-5 md:gap-5 md:p-6 xl:gap-6 min-[2560px]:gap-8 min-[2560px]:p-8"
                  style={{ backgroundColor: colors.panelDark }}
                >
                  {/* Glow */}
                  <span className="pointer-events-none absolute -top-10 -left-10 h-40 w-40 rounded-full blur-3xl" style={{ backgroundColor: `${colors.purpleSoft}4D` }} />
                  <span className="pointer-events-none absolute bottom-0 right-0 h-40 w-40 rounded-full blur-3xl" style={{ backgroundColor: `${colors.purpleAlt}1A` }} />

                  <h3 className="w-full font-rajdhani text-xl sm:text-3xl md:text-3xl lg:text-3xl xl:text-4xl font-semibold text-purple drop-shadow-[0_2px_6px_rgba(116,77,255,0.45)]">
                    {card.heading}
                  </h3>

                  <div>
                    {card.points.map((point) => (
                      <h6 key={point} className="w-full font-gilroy text-sm sm:text-lg md:text-xl lg:text-xl xl:text-xl text-white/80">
                        {point}
                      </h6>
                    ))}
                  </div>
                </div>
              </motion.div>

            ))}
          </motion.div>
        </div>
        )}
      </div>

      {/* ================= SECOND SECTION ================= */}
      {showHowWeWork && (
      <div className="mx-auto w-full max-w-8xl mt-8 lg:mt-20">
        <div>

          <div className="flex justify-center">
            <TitleHeader
              badgeText="How We Work"
              titleLines={['Principles That Define Our Delivery']}
              wrapperClassName="w-full max-w-4xl flex flex-col items-center text-center"
              titleClassName="text-2xl md:text-4xl lg:text-6xl font-rajdhani font-semibold text-center"
              badgeClassName="text-2xl md:text-2xl px-8 md:px-10 py-2.5 md:py-3.5"
            />
          </div>

          {/* Marquee */}
          <div className="w-full max-w-8xl mx-auto overflow-hidden pb-3">
            <div className="flex w-max gap-8 will-change-transform animate-[workMarquee_34s_linear_infinite] min-[2560px]:animate-[workMarquee_42s_linear_infinite]">
              
              {[0, 1, 2].map((loopIndex) => (
                <div key={loopIndex} className="flex shrink-0 gap-8">
                  
                  {workSecondSectionCards.map((card, index) => (
                    <div
                      key={`${card.heading}-${loopIndex}-${index}`}
                      className="relative w-[280px] shrink-0 pt-10 md:w-[350px] lg:w-[500px] xl:w-[450px] min-[2560px]:w-[680px] transition-transform duration-300 hover:scale-[1.05]"
                    >
                      
                      {/* Number Badge */}
                      <div
                        className="absolute left-6 top-10 z-10 flex w-8 h-8 md:h-14 md:w-14 -translate-y-1/2 items-center justify-center rounded-full bg-purple font-gilroy text-xl font-semibold text-white"
                        style={{ boxShadow: `0 0 0 4px ${colors.deepNavy}` }}
                      >
                        {index + 1}
                      </div>

                      {/* Border */}
                      <div
                        className="rotating-border-gradient h-full rounded-[30px] p-px"
                      >
                        {/* Card */}
                        <div
                          className="relative flex h-[220px] w-full flex-col justify-center md:gap-4 overflow-hidden rounded-[30px] py-8 px-5 md:p-6 text-left transition-all duration-300 md:h-[250px] lg:h-[300px] xl:h-[260px] min-[2560px]:h-[420px]"
                          style={{ backgroundColor: colors.panelDark }}
                        >
                          {/* Glow */}
                          <span className="pointer-events-none absolute -top-10 -left-10 h-40 w-40 rounded-full blur-3xl" style={{ backgroundColor: `${colors.purpleSoft}4D` }} />
                          <span className="pointer-events-none absolute bottom-0 right-0 h-40 w-40 rounded-full blur-3xl" style={{ backgroundColor: `${colors.purpleAlt}1A` }} />

                          <h4 className="w-full font-rajdhani text-xl font-semibold text-white md:text-2xl lg:text-2xl">
                            {card.heading}
                          </h4>

                          <p className="w-full font-gilroy text-xs leading-relaxed text-gray-300 md:text-sm lg:text-base">
                            {card.text}
                          </p>
                        </div>
                      </div>

                    </div>
                  ))}

                </div>
              ))}

            </div>
          </div>

        </div>
      </div>
      )}

    </section>
  )
}

export default Work
